import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SecurityContext,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AppService } from '@app/app.service';
import { enumOcrStatus } from '@app/data-layer/enums/common';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { CompanyModel } from '@app/data-layer/models/companies';
import { DocumentCreateModel, DocumentModel, DocumentPreviewModel } from '@app/data-layer/models/documents';
import { DEFAULT_MAX_POLLING_RETRY, DEFAULT_POLLING_TIMEOUT } from '@constants/constants';
import { IDefaultSelect } from '@interfaces/default-select';
import { CompaniesService } from '@services/companies.service';
import { DocumentsService } from '@services/documents.service';
import { from, interval, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, switchMap, take, takeUntil, takeWhile, tap, toArray } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ShipmentModel } from '@app/data-layer/models/common';
import { enumMimeType } from '@enum/enumMimeType';
import { enumFileExtension } from '@enum/enumFileExtension';
import { UtilsHelper } from '@helpers/utils-helper';
import { DocumentsRepository } from '@app/data-layer/repositories';
import { IsfModel } from '@app/data-layer/models/isf';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { ALLOWED_FILE_EXTENSIONS } from '@constants/constants';
import { environment } from '@env/environment';
import { OverflowListenerDirective } from '@shared/directives/directives/overflow-listener.directive';

@Component({
  selector: 'app-isf-docs-strip',
  templateUrl: './isf-docs-strip.component.html',
  styleUrls: ['./isf-docs-strip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IsfDocsStripComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('pendingNonOcrFileInput') pendingNonOcrFileInput: ElementRef<HTMLInputElement>;
  @ViewChild(OverflowListenerDirective) overflowListener: OverflowListenerDirective;
  @ViewChildren('sectionElement') sectionElements!: QueryList<ElementRef>;
  @Input() public isf: IsfModel;
  @Input() public shipments: ShipmentModel[];
  @Input() public topOffset: number = 65;

  public company: CompanyModel;
  public isLoading: boolean = false;
  public isForceAddDocument: boolean = false;
  public isAdditionalPseudoDoc: boolean = false;
  public isInvoiceReplace: boolean = false;
  public isOpenUploadModal: boolean = false;
  public isOpenDocsPreviewModal: boolean = false;
  public isIsfLoadingFirstTime: boolean = true;
  public docsPreviewUrls: Map<number, string> = new Map();
  public previewModalDocs: DocumentPreviewModel[] = [];
  public currentPreviewModalDocIndex: number = 0;
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public isLoadingDocUploadFlags: Map<string, boolean> = new Map();
  public isLoadingAdditionalDocUploadFlags: Map<string, boolean> = new Map();
  public defaultBlDocuments: enumDocumentType[] = [enumDocumentType.ISF_FORM];

  public additionalDocumentSelectItems: IDefaultSelect[] = [];
  public additionalDocumentOptions: enumDocumentType[] = [enumDocumentType.BILL_OF_LADING, enumDocumentType.UNKNOWN];

  public scannableAdditionalDocs: enumDocumentType[] = [enumDocumentType.BILL_OF_LADING, enumDocumentType.UNKNOWN];

  public skipOcrScanControl: UntypedFormControl;
  public currentDocument: DocumentModel;
  public currentDocumentNumber: number;
  public isDocStripOverflowing: boolean = false;
  public isDocStripScrolledToStart: boolean = true;
  public isDocStripScrolledToEnd: boolean = false;
  public fileMimeIgnoreList: enumMimeType[] = [enumMimeType.XLS];
  public fileExtensionIgnoreList: enumFileExtension[] = [enumFileExtension.XLS];
  public documentChangeCounter: number = 0;
  public pendingDroppedFile: File | null = null;
  public isShowInvalidFileToast: boolean = false;
  public invalidFileErrorMessage: string = '';
  public dragOverCardKey: string | null = null;
  public pendingAdditionalDocuments: DocumentModel[] = [];

  private ocrStatus: enumOcrStatus;
  private isfDocuments: DocumentModel[] = [];
  private defaultScrollDistance = 200;
  private pendingNonOcrDoc: DocumentModel | null = null;
  private overflowCheckPending: boolean = false;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private documentsRepository: DocumentsRepository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private isfStateService: IsfStateService,
  ) {}

  ngOnInit(): void {
    this.skipOcrScanControl = new UntypedFormControl(false);

    this.additionalDocumentSelectItems = this.additionalDocumentOptions.map((type, i) => {
      const isIsfForm = type === enumDocumentType.ISF_FORM;
      const documentTypeName = enumDocumentType[type];

      return {
        id: i,
        value: type,
        label: isIsfForm ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`,
      };
    });
    this.company = this.companiesService.getCompany();
    this.isLoading = true;

    this.isfStateService.isf$.pipe(takeUntil(this.destroy$)).subscribe((isf) => {
      this.isf = isf;
      if (!!this.isf && !!this.isf?.id && this.isIsfLoadingFirstTime) {
        this.getIsfDocuments(true)
          .pipe(
            switchMap(() => this.getIsfDocumentsPreviewUrls(false)),
            finalize(() => {
              this.isLoading = false;
              this.cdr.detectChanges();
            }),
            takeUntil(this.destroy$),
          )
          .subscribe(
            (docsPreviewUrls) => {
              this.setIsfDocumentsPreviewUrls(docsPreviewUrls);
              this.isLoading = false;
              this.isIsfLoadingFirstTime = false;
            },
            () => (this.isLoading = false),
          );
      }
      this.cdr.detectChanges();
    });

    this.isfStateService.currentIsfDocOCRState$.pipe(takeUntil(this.destroy$)).subscribe((ocrStatus: enumOcrStatus) => {
      this.ocrStatus = ocrStatus;
      this.cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shipments) {
      const previousShipmentsValue = changes.shipments.previousValue;
      const currentShipmentsValue = changes.shipments.currentValue;

      if (previousShipmentsValue?.length !== currentShipmentsValue?.length && !changes.shipments.firstChange) {
        this.resetScrollingContainerParams();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  ngAfterViewChecked(): void {
    if (this.overflowCheckPending && this.overflowListener) {
      this.overflowCheckPending = false;
      setTimeout(() => {
        if (this.overflowListener) {
          this.overflowListener.recalculate();
          this.cdr.markForCheck();
        }
      });
    }
  }

  public onDocumentUploadClick(document: DocumentModel, isForceAdd?: boolean, isAdditionalPseudoDoc?: boolean, isInvoiceReplace?: boolean) {
    if (this.isLoading || this.isDocumentUploadLoading(document)) {
      return;
    }

    const documentsOfType = (this.isfDocuments || []).filter((doc) => doc.type === document.type);
    const indexOfCurrentDocumentOfType = documentsOfType.findIndex((doc) => doc.id === document.id);
    const documentNumber =
      (isAdditionalPseudoDoc || indexOfCurrentDocumentOfType === -1) && !isInvoiceReplace
        ? documentsOfType.length + 1
        : indexOfCurrentDocumentOfType + 1;

    this.documentChangeCounter++;
    this.currentDocument = document;
    this.currentDocumentNumber = documentNumber;
    this.isForceAddDocument = isForceAdd;
    this.isAdditionalPseudoDoc = isAdditionalPseudoDoc;
    this.isInvoiceReplace = isInvoiceReplace;
    if (this.isDocumentUploaded(document)) {
      return;
    }

    this.setIsOpenUploadModal(true);
  }

  public onPreviewDocument(document: DocumentModel) {
    this.currentPreviewModalDocIndex = this.isfPreviewModalDocs.findIndex((pmd) => pmd.id === document.id);
    this.setIsOpenDocsPreviewModal(true);
  }

  public onPreviewDocumentDelete(data: { docId: number; shipmentId: number }) {
    const { docId, shipmentId: entityId } = data;

    let doc: DocumentModel;
    const currentShipment = this.isf?.shipment;
    const invoices = currentShipment?.invoices;
    const invoice = invoices?.find((i) => i.id === entityId);

    if (entityId === this.isf?.id) {
      doc = [...Object.values(this.isfDocuments)].find((doc: DocumentModel) => {
        return doc.rootEntityId === this.isf?.id && doc.id === docId;
      });
    } else if (!!invoice) {
      doc = [...Object.values(this.isfDocuments)].find((doc: DocumentModel) => {
        return doc.relatedEntityId === entityId && doc.id === docId;
      });
    } else {
      doc = this.isfDocuments.find((doc) => doc.id === docId);
    }

    if (!doc) {
      return;
    }

    this.deleteDocument(doc);
  }

  public onAdditionalDocClick() {
    this.documentChangeCounter++;
  }

  public onMultipleDocTypeSelect(type: enumDocumentType) {
    const serverDocsOfType = (this.isfDocuments || []).filter((doc) => doc.type === type).length;
    const pendingDocsOfType = this.pendingAdditionalDocuments.filter((doc) => doc.type === type).length;
    const docNumber = serverDocsOfType + pendingDocsOfType + 1;

    const document: DocumentModel = {
      id: docNumber + this.isf.id,
      rootEntityId: this.isf.id,
      rootEntityType: enumDocumentRootEntityType.ISF,
      relatedEntityId: this.isf.id,
      relatedEntityType: enumDocumentRelatedEntityType.ISF,
      url: null,
      createdDate: null,
      fileName: null,
      type,
      state: null,
    };

    this.pendingAdditionalDocuments.push(document);
    this.isDocStripScrolledToEnd = false;
    this.overflowCheckPending = true;
    this.cdr.markForCheck();
  }

  public closeOpenedSidebar() {
    this.appService.closeSidebarAsWindow();
  }

  public document(doc: DocumentModel): DocumentModel {
    if (!this.isfDocuments) {
      return;
    }

    return this.isfDocuments.find((document: DocumentModel) => {
      return document.id === doc.id && doc.relatedEntityId === this.isf.id;
    });
  }

  public documentPreviewUrl(doc: DocumentModel): string {
    if (!this.isfDocuments || !this.docsPreviewUrls) {
      return;
    }

    return this.docsPreviewUrls.get(doc.id);
  }

  public documentLabel(type: enumDocumentType): string {
    const isIsfForm = type === enumDocumentType.ISF_FORM;
    const documentTypeName = enumDocumentType[type];
    return isIsfForm ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`;
  }

  public documentName(doc: DocumentModel, docNumber?: number): string {
    const currentShipmentOfTypeDocs = (this.isfDocuments || []).filter((cdotd) => {
      return cdotd.type === doc.type;
    });

    const indexOfCurrentShipmentOfTypeDoc = currentShipmentOfTypeDocs.findIndex((cdotd) => cdotd.id === doc.id);
    const documentTypeName = enumDocumentType[doc.type];
    const isIsfForm = doc.type === enumDocumentType.ISF_FORM;
    const title = isIsfForm ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`;

    // For server docs found in isfDocuments: use the index
    if (indexOfCurrentShipmentOfTypeDoc > 0) {
      return this.translateService.instant(title) + ` #${indexOfCurrentShipmentOfTypeDoc + 1}`;
    }

    // For pending docs (not found in isfDocuments): use the passed docNumber
    if (docNumber && docNumber > 1) {
      return this.translateService.instant(title) + ` #${docNumber}`;
    }

    return title;
  }

  public documentUploadModalHeaderDocumentName(doc: DocumentModel, docNumber?: number) {
    const documentTypeName = enumDocumentType[doc.type];
    const countOfDocumentType = this.isfDocuments.filter((iDoc) => iDoc.type === doc.type)?.length || 0;
    const isIsfForm = doc.type === enumDocumentType.ISF_FORM;
    const docNumberText = countOfDocumentType <= 1 ? '' : ` #${docNumber}`;
    return this.translateService.instant(isIsfForm ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`) + docNumberText;
  }

  public isDocumentUploadLoading(doc?: DocumentModel): boolean {
    return this.isLoadingDocUploadFlags.get(doc.type + '-' + this.isf.id + '-' + doc.id);
  }

  public isDocumentUploaded(doc: DocumentModel): boolean {
    return doc?.state === enumDocumentState.UPLOADED;
  }

  public getPendingAdditionalDocs(): DocumentModel[] {
    return this.pendingAdditionalDocuments;
  }

  public trackByPendingDoc(index: number, doc: DocumentModel): number {
    return doc.id;
  }

  public pendingDocNumber(doc: DocumentModel): number {
    const serverDocsOfType = (this.isfDocuments || []).filter((d) => d.type === doc.type).length;
    const pendingOfType = this.pendingAdditionalDocuments.filter((d) => d.type === doc.type);
    const pendingIndexOfType = pendingOfType.findIndex((d) => d.id === doc.id);
    return serverDocsOfType + pendingIndexOfType + 1;
  }

  public isPendingAdditionalDoc(doc: DocumentModel): boolean {
    return this.pendingAdditionalDocuments.some((d) => d.id === doc.id);
  }

  public removePendingAdditionalDoc(doc: DocumentModel): void {
    this.pendingAdditionalDocuments = this.pendingAdditionalDocuments.filter((d) => d.id !== doc.id);
    this.overflowCheckPending = true;
    this.cdr.markForCheck();
  }

  public onPendingAdditionalDocClick(doc: DocumentModel): void {
    if (this.pendingNonOcrDoc !== null) {
      return;
    }
    this.pendingNonOcrDoc = doc;
    this.pendingNonOcrFileInput?.nativeElement?.click();
    this.cdr.markForCheck();
  }

  public onPendingNonOcrFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    const doc = this.pendingNonOcrDoc;
    this.pendingNonOcrDoc = null;
    if (input) {
      input.value = '';
      input.blur();
    }
    if (!file || !doc) {
      this.cdr.markForCheck();
      return;
    }
    const result = this.validateDroppedFile(file);
    if (!result.valid) {
      this.invalidFileErrorMessage = result.errorMessage ?? '';
      this.isShowInvalidFileToast = true;
      this.cdr.markForCheck();
      return;
    }
    this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${doc.id}`, true);
    this.uploadAdditionalDocumentToServer(file, doc.type, doc);
    this.cdr.markForCheck();
  }

  public pendingDocumentName(doc: DocumentModel): string {
    const docNumber = this.pendingDocNumber(doc);
    const documentTypeName = enumDocumentType[doc.type];
    const isIsfForm = doc.type === enumDocumentType.ISF_FORM;
    const title = this.translateService.instant(isIsfForm ? 'LABELS.ISF_INSTRUCTION_SHEET' : `DOCUMENT_TYPE.${documentTypeName}`);
    return docNumber > 1 ? `${title} #${docNumber}` : title;
  }

  public getDropZoneKey(doc: DocumentModel): string {
    return `${doc.type}-${doc.id}`;
  }

  public validateDroppedFile(file: File): { valid: boolean; errorMessage?: string } {
    const allowedExtensions = ALLOWED_FILE_EXTENSIONS.filter((ext) => !this.fileExtensionIgnoreList.includes(ext as enumFileExtension));
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!allowedExtensions.includes(extension as enumFileExtension)) {
      return {
        valid: false,
        errorMessage: this.translateService.instant('FORM_ERROR.FILE_EXTENSION_NOT_SUPPORTED', { fileName: file.name }),
      };
    }
    const fileSizeMB = file.size / 1024 / 1024;
    const maxMB = environment.files.default_file_size_limit;
    if (fileSizeMB > maxMB) {
      return {
        valid: false,
        errorMessage: this.translateService.instant('FORM_ERROR.FILE_SIZE_EXCEEDS_LIMIT', { fileName: file.name }),
      };
    }
    return { valid: true };
  }

  public onDragOver(event: DragEvent, doc: DocumentModel): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isDocumentUploaded(doc)) return;
    this.dragOverCardKey = this.getDropZoneKey(doc);
    this.cdr.markForCheck();
  }

  public onDrop(event: DragEvent, doc: DocumentModel): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverCardKey = null;
    this.cdr.markForCheck();
    if (this.isDocumentUploaded(doc)) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }
    if (files.length !== 1) {
      this.invalidFileErrorMessage = this.translateService.instant('FORM_ERROR.DROP_ONE_FILE_ONLY');
      this.isShowInvalidFileToast = true;
      this.cdr.markForCheck();
      return;
    }

    const file = files[0];
    const result = this.validateDroppedFile(file);
    if (!result.valid) {
      this.invalidFileErrorMessage = result.errorMessage ?? '';
      this.isShowInvalidFileToast = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.isPendingAdditionalDoc(doc)) {
      this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${doc.id}`, true);
      this.uploadAdditionalDocumentToServer(file, doc.type, doc);
      this.cdr.markForCheck();
      return;
    }
    this.setCurrentDocumentForUpload(doc);
    this.pendingDroppedFile = file;
    this.setIsOpenUploadModal(true);
    this.cdr.markForCheck();
  }

  public onDragLeave(): void {
    this.dragOverCardKey = null;
    this.cdr.markForCheck();
  }

  public dismissInvalidFileToast(): void {
    this.isShowInvalidFileToast = false;
    this.invalidFileErrorMessage = '';
    this.cdr.markForCheck();
  }

  public uploadDocument(file: File, doc: DocumentModel, isForceAdd?: boolean, isAdditionalPseudoDoc?: boolean): void {
    if (this.isf?.id) {
      this.uploadDocumentToServer(file, doc, isForceAdd, isAdditionalPseudoDoc);
      return;
    }
  }

  public uploadAdditionalDocument(event: { file: File; type: enumDocumentType }): void {
    const file = event.file;
    const type = event.type;

    if (this.isf?.id) {
      this.uploadAdditionalDocumentToServer(file, type);
      return;
    }
  }

  public deleteDocument(doc: DocumentModel): void {
    if (this.isf?.id) {
      this.deleteDocumentFromServer(doc);
      return;
    }
  }

  public updateDocumentState(state: enumDocumentState, doc: DocumentModel): void {
    if (!this.isfDocuments) {
      return;
    }

    this.isfDocuments = this.isfDocuments.map((document: DocumentModel) =>
      document.id === doc?.id
        ? {
            ...document,
            state,
          }
        : document,
    );

    this.setDocumentIsLoading(`${doc?.type}-${this.isf.id}-${doc?.id}`, false);
    this.isLoading = false;
  }

  public onBrowse(file: File) {
    if (!this.currentDocument) {
      this.setIsOpenUploadModal(false);
      this.resetDocumentUploadFields();
      return;
    }

    this.pendingDroppedFile = null;
    this.setIsOpenUploadModal(false);
    this.uploadDocument(file, this.currentDocument, this.isForceAddDocument, this.isAdditionalPseudoDoc);
    setTimeout(() => {
      this.resetDocumentUploadFields();
    }, 500);
  }

  public onOverflowChange(change) {
    const { isOverflowingHorizontally, isOverflowingVertically } = change;
    this.isDocStripOverflowing = isOverflowingHorizontally;
  }

  public onScrolledToStart() {
    this.isDocStripScrolledToEnd = false;
    this.isDocStripScrolledToStart = true;
  }

  public onScrolling() {
    this.isDocStripScrolledToEnd = false;
    this.isDocStripScrolledToStart = false;
  }

  public onScrolledToEnd() {
    this.isDocStripScrolledToStart = false;
    this.isDocStripScrolledToEnd = true;
  }

  public scrollToDirection(direction: 'left' | 'right') {
    const scrollAmount: number = this.defaultScrollDistance;
    let directionModifier: number;

    switch (direction) {
      case 'left':
        directionModifier = -1;
        break;
      case 'right':
        directionModifier = 1;
        break;
      default:
        break;
    }

    this.scrollByAmount(scrollAmount * directionModifier, 0);
  }

  public setIsOpenUploadModal(state: boolean) {
    this.isOpenUploadModal = state;
  }

  public setIsOpenDocsPreviewModal(state: boolean) {
    this.isOpenDocsPreviewModal = state;
  }

  public get documentUploadingStatusText(): string {
    const scanningStatuses = [enumOcrStatus.PROCESSING, enumOcrStatus.PARTIALLY_PROCESSED, enumOcrStatus.CLASSIFYING_LINE_ITEMS];
    return scanningStatuses.includes(this.ocrStatus) ? 'LABELS.SCANNING' : 'LABELS.UPLOADING';
  }

  public get documentUploadModalHeader(): string {
    let documentName: string = '';
    if (!this.currentDocument) {
      return this.translateService.instant('DOCUMENT_UPLOAD_MODAL.HEADER');
    }

    documentName = this.translateService.instant(
      this.documentUploadModalHeaderDocumentName(this.currentDocument, this.currentDocumentNumber),
    );

    return this.translateService.instant('DOCUMENT_UPLOAD_MODAL.HEADER', { documentName });
  }

  public get isHideScrollLeft(): boolean {
    return !this.isDocStripOverflowing || this.isDocStripScrolledToStart;
  }

  public get isHideScrollRight(): boolean {
    return !this.isDocStripOverflowing || this.isDocStripScrolledToEnd;
  }

  public get isfPreviewModalDocs(): DocumentPreviewModel[] {
    return this.previewModalDocs?.filter((pmd, index, self) => {
      return index === self.findIndex((p) => p.id === pmd.id);
    });
  }

  private setCurrentDocumentForUpload(document: DocumentModel): void {
    const documentsOfType = (this.isfDocuments || []).filter((d) => d.type === document.type);
    const indexOfCurrentDocumentOfType = documentsOfType.findIndex((d) => d.id === document.id);
    const documentNumber = indexOfCurrentDocumentOfType === -1 ? documentsOfType.length + 1 : indexOfCurrentDocumentOfType + 1;

    this.documentChangeCounter++;
    this.currentDocument = document;
    this.currentDocumentNumber = documentNumber;
    this.isForceAddDocument = false;
    this.isAdditionalPseudoDoc = false;
    this.isInvoiceReplace = false;
  }

  private resetDocumentUploadFields() {
    this.currentDocument = null;
    this.currentDocumentNumber = null;
    this.isForceAddDocument = null;
    this.isAdditionalPseudoDoc = null;
    this.isInvoiceReplace = null;
    this.pendingDroppedFile = null;

    if (!this.isOpenUploadModal) {
      this.skipOcrScanControl.reset();
    }
  }

  private resetScrollingContainerParams() {
    this.isDocStripScrolledToStart = true;
    this.isDocStripScrolledToEnd = false;

    const container: HTMLElement = this.scrollContainer.nativeElement;
    container.scrollTo({
      left: 0,
    });
  }

  private uploadDocumentToServer(file: File, doc: DocumentModel, isForceAdd?: boolean, isAdditionalPseudoDoc?: boolean): void {
    this.ocrStatus = null;

    const typesToSkipOcrScan = [enumDocumentType.BILL_OF_LADING, enumDocumentType.UNKNOWN];

    const isStartIsfOcrScan = !this.skipOcrScanControl.value && !typesToSkipOcrScan.includes(doc.type);

    let createdDocId: number;
    let createdDocData: { file: File; docId: number; docHash: string };

    this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${doc.id}`, true);

    const isDocAlreadyExist = !!this.isfDocuments.find((document) => document.id === doc.id);

    let uploadDocument$: Observable<number>;
    let documentToCreate: DocumentCreateModel = {
      fileName: file.name,
      rootEntityId: this.isf?.id,
      rootEntityType: enumDocumentRootEntityType.ISF,
      relatedEntityId: this.isf?.id,
      relatedEntityType: enumDocumentRelatedEntityType.ISF,
      type: Number(doc.type),
    };

    if (isStartIsfOcrScan) {
      uploadDocument$ = this.createDocument(documentToCreate, file, this.company?.id).pipe(
        switchMap((data) => {
          createdDocData = data;
          return of(data.docId);
        }),
      );
    } else {
      uploadDocument$ = this.isfStateService.uploadDocument(documentToCreate, file, false, this.company?.id);
    }

    uploadDocument$
      .pipe(
        takeUntil(this.destroy$),
        tap((docId) => (createdDocId = docId)),
        switchMap(() => {
          if (isStartIsfOcrScan) {
            return this.isfStateService.processIsfDocOCR(this.isf?.id, createdDocData, true);
          } else {
            return of(enumOcrStatus.PROCESSED);
          }
        }),
        tap(() => {
          if (isForceAdd) {
            this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${createdDocId}`, true);
            this.cdr.detectChanges();
          }
        }),
        switchMap(() => {
          if (!isDocAlreadyExist && isAdditionalPseudoDoc) {
            this.removePendingAdditionalDoc(doc);
          }
          return this.syncIsfDocument(createdDocId, true);
        }),
      )
      .subscribe((isf) => {
        this.isfStateService
          .updateDocuments(false)
          .pipe(
            switchMap(() => this.isfStateService.updateDocumentsState(false)),
            switchMap(() => this.isfStateService.loadIsf()),
            switchMap((isf) => this.isfStateService.updateIsf(isf)),
            tap((isf) => this.isfStateService.updateCurrentIsf(isf)),
            take(1),
          )
          .subscribe(() => {
            this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${createdDocId}`, false);
            this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${doc.id}`, false);
            this.isLoading = false;
            if (!this.isOpenUploadModal) {
              this.skipOcrScanControl.reset();
            }
            this.cdr.detectChanges();
          });

        if (isStartIsfOcrScan) {
          this.isfOcrQuickFixService.dataSaved$.next();
        }
        //start polling for document preview urls
        setTimeout(() => {
          this.pollIsfDocumentsPreviewUrls(createdDocId)
            .pipe(
              switchMap(() => this.isfStateService.updateDocuments(false)),
              finalize(() => {
                this.isLoading = false;
                this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${createdDocId}`, false);
                this.cdr.detectChanges();
              }),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }, DEFAULT_POLLING_TIMEOUT); //adding delay to give document time to upload before getting downlonad url
      });
  }

  private uploadAdditionalDocumentToServer(file: File, type: enumDocumentType, pendingDoc?: DocumentModel): void {
    let createdDocId: number;
    if (pendingDoc) {
      this.setDocumentIsLoading(`${pendingDoc.type}-${this.isf.id}-${pendingDoc.id}`, true);
    } else {
      this.setAdditionalDocumentIsLoading(`isf-additional-doc-input-${this.isf.id}`, true);
    }
    this.isfStateService
      .uploadDocument(
        {
          fileName: file.name,
          rootEntityId: this.isf?.id,
          rootEntityType: enumDocumentRootEntityType.ISF,
          relatedEntityId: this.isf?.id,
          relatedEntityType: enumDocumentRelatedEntityType.ISF,
          type: Number(type),
        },
        file,
        false,
        this.company?.id,
      )
      .pipe(
        tap((docId) => (createdDocId = docId)),
        switchMap(() => this.isfStateService.loadIsf(this.isf.referenceNumber)),
        switchMap((isf) => of(this.isfStateService.updateCurrentIsf(isf))),
        switchMap(() => this.getIsfDocuments(false)),
        takeUntil(this.destroy$),
        finalize(() => {
          if (pendingDoc) {
            this.setDocumentIsLoading(`${pendingDoc.type}-${this.isf.id}-${pendingDoc.id}`, false);
            this.removePendingAdditionalDoc(pendingDoc);
          } else {
            this.setAdditionalDocumentIsLoading(`isf-additional-doc-input-${this.isf.id}`, false);
          }

          this.isfStateService
            .updateDocuments(true)
            .pipe(take(1))
            .subscribe(() => {
              this.cdr.detectChanges();
            });

          this.cdr.detectChanges();
          this.scrollByAmount(this.defaultScrollDistance);
        }),
      )
      .subscribe((docs) => {
        //start polling for document preview urls
        setTimeout(() => {
          this.pollIsfDocumentsPreviewUrls(createdDocId)
            .pipe(
              finalize(() => {
                if (!pendingDoc) {
                  this.setAdditionalDocumentIsLoading(`isf-additional-doc-input-${this.isf.id}`, false);
                }
                this.cdr.detectChanges();
              }),
              takeUntil(this.destroy$),
            )
            .subscribe();
        }, DEFAULT_POLLING_TIMEOUT); //adding delay to give document time to upload before getting downlonad url
      });
  }

  private deleteDocumentFromServer(doc: DocumentModel): void {
    this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${doc.id}`, true);

    this.documentsService
      .deleteDocument(doc.id, false)
      .pipe(
        switchMap(() => this.getIsfDocuments(false)),
        switchMap(() => this.isfStateService.updateDocuments(true)),
        switchMap(() => this.isfStateService.loadIsf(this.isf.referenceNumber).pipe(take(1))),
        finalize(() => {
          this.setDocumentIsLoading(`${doc.type}-${this.isf.id}-${doc.id}`, false);
          this.previewModalDocs = this.previewModalDocs.filter((pmd) => pmd.id !== doc.id);

          this.cdr.detectChanges();
          this.scrollByAmount(-this.defaultScrollDistance);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((isf) => {
        this.isfStateService.updateCurrentIsf(isf);
        this.cdr.detectChanges();
      });
  }

  private createDocument(
    document: DocumentCreateModel,
    file: File,
    companyId?: number,
    isShowLoader: boolean = false,
  ): Observable<{ file: File; docId: number; docHash: string }> {
    const fakeDocHash: string = new Date().toLocaleDateString();
    this.documentsService.isUploadingDocIdsToPush$.next(fakeDocHash);

    return this.documentsRepository
      .create(companyId || this.companiesService.getCompany().id, { ...document, fileName: file.name }, isShowLoader)
      .pipe(
        map((doc) => {
          return {
            file,
            docId: doc.id,
            docHash: fakeDocHash,
          };
        }),
      );
  }

  private getIsfDocument(docId: number, isShowLoader: boolean): Observable<DocumentModel> {
    return this.documentsService
      .getIsfDocuments(this.isf?.id, isShowLoader)
      .pipe(map((documents: DocumentModel[]) => documents.find((doc) => doc.id === docId) || null));
  }

  private getIsfDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getIsfDocuments(this.isf?.id, isShowLoader).pipe(
      tap((documents: DocumentModel[]) => {
        this.syncIsfDocuments(documents);
      }),
    );
  }

  private getIsfDocumentsPreviewUrls(isShowLoader: boolean): Observable<DocumentPreviewModel[]> {
    const isfDocumentsValues = [...Object.values(this.isfDocuments)];
    const isfDocuments = isfDocumentsValues.reduce((acc, curr) => acc.concat(curr), []);
    return of(
      isfDocuments.filter((doc) => {
        return doc.id > this.defaultBlDocuments.length;
      }),
    ).pipe(
      filter((docs: DocumentModel[]) => !!docs?.length),
      mergeMap((docs) => from(docs)),
      mergeMap((doc) => {
        if (!!doc?.previewDocumentId) {
          return this.documentsService.getDownloadLink(doc?.previewDocumentId, isShowLoader).pipe(
            map((response) => {
              let invoiceShipmentId: number;

              if (doc.type === enumDocumentType.INVOICE) {
                const invoiceShipment = this.shipments.find((shipment) => shipment.invoices.find((inv) => inv.id === doc.relatedEntityId));
                invoiceShipmentId = invoiceShipment?.id;
              }

              const shipmentId = doc.type === enumDocumentType.INVOICE ? invoiceShipmentId : doc.relatedEntityId;

              return {
                id: doc.id,
                url: response.downloadUrl,
                shipmentId,
                type: doc.type,
              };
            }),
            catchError((error) => {
              //return pseudo object to prevent polling interruptions
              return of({
                id: -999,
                url: '',
                shipmentId: -1000,
                type: enumDocumentType.INVOICE,
              });
            }),
          );
        } else {
          return of({
            id: -999,
            url: '',
            shipmentId: -1000,
            type: enumDocumentType.INVOICE,
          });
        }
      }),
      toArray(),
    );
  }

  private pollIsfDocumentsPreviewUrls(createdDocId: number): Observable<DocumentPreviewModel[]> {
    let pollAttempts = 0;
    this.previewModalDocs = this.isfPreviewModalDocs;

    return interval(DEFAULT_POLLING_TIMEOUT).pipe(
      switchMap(() => this.getIsfDocuments(false)),
      switchMap(() => this.getIsfDocumentsPreviewUrls(false)),
      takeWhile((docsPreviewUrls) => {
        const uploadedDocPreview: DocumentPreviewModel = docsPreviewUrls.find((dpu) => dpu.id === createdDocId && !!dpu.url);
        if (uploadedDocPreview) {
          const sanitizedPreviewBgUrl = this.sanitizer.sanitize(SecurityContext.STYLE, `url("${uploadedDocPreview.url}")`);
          this.setDocsPreviewUrls(uploadedDocPreview.id, sanitizedPreviewBgUrl);
          this.previewModalDocs.push({
            id: uploadedDocPreview.id,
            url: sanitizedPreviewBgUrl,
            shipmentId: uploadedDocPreview.shipmentId,
            type: uploadedDocPreview.type,
          });
        }

        pollAttempts++;
        this.cdr.detectChanges();
        return pollAttempts < DEFAULT_MAX_POLLING_RETRY && !uploadedDocPreview;
      }),
    );
  }

  private setIsfDocumentsPreviewUrls(docsPreviewUrls: DocumentPreviewModel[]) {
    const previewModalDocs = [];
    docsPreviewUrls.forEach((dpu) => {
      if (!!dpu.url) {
        const sanitizedPreviewBgUrl = this.sanitizer.sanitize(SecurityContext.STYLE, `url('${dpu.url}')`);
        this.setDocsPreviewUrls(dpu.id, sanitizedPreviewBgUrl);
        previewModalDocs.push({
          id: dpu.id,
          url: sanitizedPreviewBgUrl,
          shipmentId: dpu.shipmentId,
          type: dpu.type,
        });
        this.cdr.detectChanges();
      }
    });

    const sortedPreviewModalDocs = this.getSortedPreviewModalDocs(previewModalDocs);
    this.previewModalDocs = sortedPreviewModalDocs;
  }

  private syncIsfDocuments(documents: DocumentModel[]) {
    this.isfDocuments = this.filteredDocuments(documents);
    this.overflowCheckPending = true;
  }

  private syncIsfDocument(docId: number, isShowLoader: boolean) {
    if (!this.isfDocuments) {
      return of(null);
    }

    return this.getIsfDocument(docId, isShowLoader).pipe(
      tap((doc) => {
        const currentShipmentDocuments = this.isfDocuments.filter((doc) => {
          return doc.type === doc.type;
        });

        const currentDocIndex = this.isfDocuments.findIndex((ed) => ed.id === doc.id);

        if (currentDocIndex === -1) {
          const farthestTypeIndex = UtilsHelper.findLastIndex(currentShipmentDocuments, 'type', doc.type);
          if (farthestTypeIndex === -1) {
            return;
          }

          currentShipmentDocuments.splice(farthestTypeIndex, 0, doc);
        } else {
          currentShipmentDocuments[currentDocIndex] = doc;
        }
      }),
    );
  }

  private filteredDocuments(documents: DocumentModel[]): DocumentModel[] {
    const typesToSkip: enumDocumentType[] = [enumDocumentType.FILE_PREVIEW];

    const skippedExcludedDocuments: DocumentModel[] = documents.filter((doc: DocumentModel) => !typesToSkip.includes(doc.type));

    let attachedDocuments: DocumentModel[] = [];

    //set the default document upload inputs
    this.defaultBlDocuments.forEach((documentType: enumDocumentType, index: number) => {
      if (
        !skippedExcludedDocuments.find((document: DocumentModel) => {
          return document.type === documentType && document.relatedEntityId === this.isf?.id;
        })
      ) {
        attachedDocuments.push({
          id: index,
          rootEntityId: this.isf?.id,
          rootEntityType: enumDocumentRootEntityType.ISF,
          relatedEntityId: this.isf?.id,
          relatedEntityType: enumDocumentRelatedEntityType.ISF,
          url: null,
          createdDate: null,
          fileName: null,
          type: documentType,
          state: null,
        });
      }
    });

    attachedDocuments.push(
      ...skippedExcludedDocuments
        .filter((doc: DocumentModel) => doc.type === enumDocumentType.ISF_FORM)
        .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
      ...skippedExcludedDocuments
        .filter((doc: DocumentModel) => doc.type === enumDocumentType.BILL_OF_LADING)
        .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
      ...skippedExcludedDocuments
        .filter((doc: DocumentModel) => doc.type === enumDocumentType.UNKNOWN)
        .sort((a, b) => this.sortDates(a.createdDate, b.createdDate)),
    );

    return attachedDocuments;
  }

  private sortDates(a: string, b: string): number {
    return new Date(a).getTime() - new Date(b).getTime();
  }

  private setDocsPreviewUrls(id: number, value: string): void {
    this.docsPreviewUrls.set(id, value);
  }

  private setDocumentIsLoading(id: string, value: boolean): void {
    this.isLoadingDocUploadFlags.set(id, value);
  }

  private setAdditionalDocumentIsLoading(id: string, value: boolean): void {
    this.isLoadingAdditionalDocUploadFlags.set(id, value);
  }

  private scrollByAmount(scrollOffset: number, timeout: number = 500) {
    setTimeout(() => {
      const container: HTMLElement = this.scrollContainer.nativeElement;
      container.scrollTo({
        left: container.scrollLeft + scrollOffset,
        behavior: 'smooth',
      });
    }, timeout);
  }

  private getSortedPreviewModalDocs(previewModalDocs: DocumentPreviewModel[]) {
    const previewModalDocsListByShipmentId = UtilsHelper.groupObjectsByField(previewModalDocs, 'shipmentId');

    const sortedPreviewModalDocs: DocumentPreviewModel[] = [];

    const typeSortingOrder = [
      enumDocumentType.POWER_OF_ATTORNEY,
      enumDocumentType.POWER_OF_ATTORNEY_TRUCKER,
      enumDocumentType.BILL_OF_LADING,
      enumDocumentType.INVOICE,
      enumDocumentType.ARRIVAL_NOTICE,
      enumDocumentType.PACKING_LIST,
      enumDocumentType.TRADE_CERTIFICATE,
      enumDocumentType.UNKNOWN,
    ];

    Object.keys(previewModalDocsListByShipmentId).forEach((key) => {
      const currentShipmentPreviewModalDocs = previewModalDocsListByShipmentId[key];
      currentShipmentPreviewModalDocs.sort((a, b) => {
        return typeSortingOrder.indexOf(a.type) - typeSortingOrder.indexOf(b.type);
      });

      sortedPreviewModalDocs.push(...currentShipmentPreviewModalDocs);
    });

    return sortedPreviewModalDocs;
  }
}
